import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Input,
  InputProps,
  useDisclosure
} from '@chakra-ui/react'
import { useRef } from 'react'
import {
  FieldPathValue,
  FieldValues,
  Path,
  useFormContext
} from 'react-hook-form'
import { AttentionIcon } from '../Icons/AttentionIcon'
import { CommonFormLabel } from './CommonFormLabel'

export type FieldByType<FormData extends FieldValues, T> = {
  [P in Path<FormData>]: T extends FieldPathValue<FormData, P> ? P : never
}[Path<FormData>]

export type FieldInputProps<T extends FieldValues, P> = {
  name: FieldByType<T, P>
  isRequired?: boolean
  label?: string
  helperText?: string | JSX.Element
  showErrorText?: boolean
  helpTooltipText?: string
  errorMessage?: string
  formControlWidth?: string
  labelIndexNumber?: number
  /**
   * 確定申告フォームで禁止文字を弾くためにペースト禁止にする
   */
  isPasteDisabled?: boolean
}

type FormTextInputProps<T extends FieldValues> = FieldInputProps<T, string> &
  InputProps

/**
 *
 * useFieldArrayでFieldを作っているときはnameが name.index.fieldの形式になる
 * これだと errors?.[name]?.messageではエラー文が取れないので、nameにピリオドを
 * 持つかどうかでuseFieldArrayによるFieldかどうかを判別してエラー文のとり方を変える
 *
 */
export const getErrorMessage = <T extends FieldValues, P>(
  name: FieldByType<T, P>
) => {
  const {
    formState: { errors }
  } = useFormContext()
  const spilittedName = name.includes('.') ? name.split('.') : undefined
  const _errorMessage = spilittedName
    ? errors?.[spilittedName[0]]?.[Number(spilittedName[1])]?.[spilittedName[2]]
        ?.message
    : errors?.[name]?.message
  return _errorMessage
}

export const FormTextInput = <T extends FieldValues = never>({
  name,
  isRequired,
  label,
  labelIndexNumber,
  helperText,
  helpTooltipText,
  showErrorText = true,
  errorMessage,
  w,
  formControlWidth,
  isPasteDisabled,
  onChange,
  ...inputProps
}: FormTextInputProps<T>): ReturnType<React.VFC> => {
  const { register } = useFormContext()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const _errorMessage = getErrorMessage(name) || errorMessage
  return (
    <FormControl
      isRequired={isRequired}
      isInvalid={_errorMessage}
      w={formControlWidth || 'auto'}
    >
      <CommonFormLabel
        label={label}
        labelIndexNumber={labelIndexNumber}
        helpTooltipText={helpTooltipText}
        isRequired={isRequired}
      />

      <Input
        type={inputProps.type || 'text'}
        {...register(name, { onChange })}
        w={w}
        {...inputProps}
        {...(isPasteDisabled && {
          onPaste: (e) => {
            e.preventDefault()
            onOpen()
          }
        })}
        required={false}
      />
      {showErrorText && (
        <FormErrorMessage>
          <AttentionIcon />
          {_errorMessage}
        </FormErrorMessage>
      )}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {isPasteDisabled && (
        <AlertDialog
          isOpen={isOpen}
          leastDestructiveRef={cancelRef}
          onClose={onClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                テキストの貼付はできません
              </AlertDialogHeader>

              <AlertDialogBody>
                非対応の文字が入力される可能性があるため、
                <br />
                確定申告書作成画面でのテキストの貼付は行えないようにさせていただいております。
                <br />
                お手数をおかけし大変申し訳ございません。
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onClose}>
                  閉じる
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </FormControl>
  )
}
